import React from 'react';
import {connect} from 'react-redux';

import PageWithoutSideMenu from 'components/PageWithoutSideMenu';
import ListContent from 'components/ListContent';
import VisibleIf from 'components/VisibleIf';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import Loading from 'components/Loading';
import {
  UsersTable,
  UserDetailModal,
  CreateMemberModal,
  CreateUserModal,
  SubNavigation,
  UserListFilters
} from './components';

import {fetchUsers, selectUsers, selectUsersLoading, MODALS} from 'ducks/users';
import {isModalOpen, openModal} from 'ducks/modals';
import {PERMISSIONS, requiresPermission} from 'ducks/auth';
import {composeActions} from 'ducks/common';

import {openModalFromUrl} from 'utils/modals';

const initialFilters: {
  isMember: boolean | null;
  search: string;
  isAdmin: boolean | null;
} = {
  isMember: null,
  search: '',
  isAdmin: null
};

class UserList extends React.Component<
  LoginRequiredProps &
    WithFiltersProps<typeof initialFilters> &
    WithPaginationProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    this.fetchUsers();
    openModalFromUrl(MODALS, this.props.openModal);
  }

  fetchUsers = () =>
    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_USERS},
      this.props.fetchUsers
    );

  onPageChange = ({offset, pageSize}) => {
    this.props.onPaginationChange({pageSize, offset}, [fetchUsers]);
  };

  openUserDetailModal = userId => {
    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_USER_DETAIL},
      () => this.props.openModal(MODALS.USER_DETAIL, {userId}, true)
    );
  };

  openCreateMemberModal = () => {
    this.props.openModal(MODALS.CREATE_MEMBER);
  };

  openCreateUserModal = () => {
    this.props.openModal(MODALS.CREATE_USER);
  };

  clearFilters = () => {
    this.props.composeActions([this.props.clearFilters, fetchUsers]);
  };

  setFilter = (field, value) => {
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchUsers()
    ]);
  };

  setToggleFilterValue = field => {
    const filters = this.props.getFilters();

    const currentValue = filters[field];

    if (currentValue) {
      this.setFilter(field, null);
    } else {
      this.setFilter(field, true);
    }
  };

  render() {
    const {
      users,
      usersLoading,
      userDetailModal,
      createMemberModal,
      createUserModal
    } = this.props;

    return (
      <PageWithoutSideMenu>
        <VisibleIf permission={PERMISSIONS.VIEW_USERS}>
          <ListContent
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}
            className="UserList">
            <ListContent.Content>
              {usersLoading && <Loading />}
              {!usersLoading && (
                <UsersTable
                  users={users}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  openUserDetailModal={this.openUserDetailModal}
                />
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <UserListFilters
                setFilter={this.setToggleFilterValue}
                getFilters={this.props.getFilters}
                clearFilters={this.clearFilters}
              />
            </ListContent.Filters>
          </ListContent>
          {userDetailModal && <UserDetailModal fetchUsers={this.fetchUsers} />}
          {createMemberModal && <CreateMemberModal />}
          {createUserModal && <CreateUserModal />}
        </VisibleIf>
      </PageWithoutSideMenu>
    );
  }
}

const mapStateToProps = state => {
  return {
    users: selectUsers(state),
    usersLoading: selectUsersLoading(state),
    userDetailModal: isModalOpen(state, MODALS.USER_DETAIL),
    createMemberModal: isModalOpen(state, MODALS.CREATE_MEMBER),
    createUserModal: isModalOpen(state, MODALS.CREATE_USER)
  };
};

const mapDispatchToProps = {
  fetchUsers,
  composeActions,
  openModal,
  requiresPermission
};

const componentWithPagination = withPagination(
  LoginRequired(UserList),
  PAGINATION.USERS_LIST,
  {
    saveToUrl: true
  }
);
const componentWithPaginationAndFilters = withFilters(
  componentWithPagination,
  FILTERS.USERS_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  componentWithPaginationAndFilters
);
